import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import API from '../../../utils/API';
import MesTable from '../../../components/Table/MesTable';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import constants from '../../../utils/constants';
import commonMethods from '../../../utils/common-methods';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import MesReceiversModal from '../../../components/MessageManager/MesReceiversModal';

const MESSENGER_TEXT_PREFIX = "MESSENGER_";

function MessageLogs(props) {
    const navigate = useNavigate();
    const { user } = props;
    const intl = useIntl();
    const permissionIds = user.permissions.map(permission => permission.id);

    if (!permissionIds.includes(constants.permissionIds.msgManagerEdit)) {
        navigate('/forbidden');
    }
    const [data, setData] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [totalDataCount, setTotalDataCount] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [dataLoaded, setDataLoaded] = useState(false);
    const [selectedIds, setSelectedIds] = useState({});
    const [sortByCol, setSortByCol] = useState('dateAndTime');
    const [sortByAsc, setSortByAsc] = useState(false);
    const [startIndexForTable, setStartIndexForTable] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [historyData, setHistoryData] = useState([]);
    const [modalData, setModalData] = useState([]);

    const tableRef = useRef();
    const tableWrapperRef = useRef();

    const loadTable = async (map, resetSelection = true) => {
        map.data = {};
        map.ids = [];
        map.sortedIds = [];
        map.filteredIds = [];
        if (resetSelection) {
            map.selectedIds = {};
        }

        try {
            const res = await API.getHistories();
            res.data.forEach((mess) => {
                map.data[mess.id] = {
                    id: mess.id,
                    message: mess.title,
                    type: mess.format,
                    groups: mess.groups.map((role) => role.name).join(', '),
                    dateAndTime: commonMethods.formatDate(mess.timeStart),
                    status: intl.formatMessage({ id: MESSENGER_TEXT_PREFIX + mess.status }),
                    checked: false,
                    receivers: mess.failedReceivers,
                };
            });
            map.filteredIds = Object.keys(map.data);
            map.ids = Object.keys(map.data);
            setDataCount(map.ids.length);
            setHistoryData(res.data);
            setTotalDataCount(map.ids.length);
            setDataLoaded(true);
        } catch (err) {
            setError(err.message);
            setDataLoaded(true);
        }
    };

    const dataMap = useMemo(() => {
        const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
        loadTable(map);
        return map;
    }, []);

    // Hook for infinite scrolling
    const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
        dataMap,
        setData,
        setStartIndexForTable,
        startIndexForTable,
        pageSize: 100,
        sortByCol,
        setSortByCol,
        sortByAsc,
        setSortByAsc,
        tableWrapperRef,
        searchValue,
        tableRef,
        rowHeight: 48,
    });

    // Hook for sorting data when data changes or sort column is changed
    useEffect(() => {
        if (dataLoaded) {
            if (searchValue !== '') {
                tableRef.current.search(dataMap);
            } else {
                sortMap(dataMap);
                setData(generateDataSlice(dataMap));
            }
        }
    }, [sortByCol, sortByAsc, dataLoaded, dataMap, historyData]);

    const handleExportCsv = () => {
        const selectedMessages = Object.values(dataMap.data).filter(mess => selectedIds[mess.id]);
        const csvData = selectedMessages.map((mess) => ({
            'Message': mess.message,
            'Type': mess.type,
            'Groups': mess.groups,
            'Date and Time': mess.dateAndTime,
            'Delivery': mess.status,
        }));

        const escapeCsvField = (field) => {
            if (typeof field === 'string') {
                return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
        };

        const csv = [
            Object.keys(csvData[0]).map(escapeCsvField).join(','), // Header row
            ...csvData.map((row) => Object.values(row).map(escapeCsvField).join(',')), // Data rows
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'message_logs.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    function handleViewReceiversClick(receivers) {
        setModalData(receivers);
    }

    const tableColumnArray = [
        {
            Header: tableCheckbox,
            id: 'checkbox',
            accessor: 'checked',
            Cell: tableCheckbox,
            isVisible: true,
            width: 63,
            maxWidth: 63,
        },
        {
            Header: 'MESSAGE',
            id: 'message',
            accessor: 'message',
            isVisible: true,
        },
        {
            Header: 'TYPE',
            id: 'type',
            accessor: 'type',
            isVisible: true,
        },
        {
            Header: 'GROUPS',
            id: 'groups',
            isVisible: true,
            accessor: 'groups',
        },
        {
            Header: 'DATE_AND_TIME',
            id: 'dateAndTime',
            accessor: 'dateAndTime',
            isVisible: true,
        },
        {
            Header: 'DELIVERY',
            id: 'status',
            accessor: 'status',
            isVisible: true,
        },
        {
            Header: 'VIEW_RECEIVERS',
            id: 'mesViewReceivers',
            Cell: ({ row }) => (
                <div
                    type="button"
                    className="link-button"
                    data-bs-toggle="modal"
                    data-bs-target="#mesReceiversModal"
                    onClick={() => handleViewReceiversClick(row.original.receivers)}
                >
                    <FormattedMessage id="VIEW_RECEIVERS" />
                </div>
            ),
            isVisible: false,
        }
    ];

    const buttons = {
        footerButtons: (
            <>
                <button
                    id="exportCsvBtn"
                    type="button"
                    className="btn"
                    onClick={handleExportCsv}
                    disabled={Object.keys(selectedIds).length === 0}
                >
                    <span><FormattedMessage id="EXPORT_AS_CSV" defaultMessage="Export As CSV" /></span>
                </button>
            </>
        ),
    };

    const tableProps = {
        // The tableKey is for task#20542.
        // Each table requires a unique key to prevent preferences from being overwritten.
        tableKey: "sbmm_logs_table",
        pageType: constants.pageType.MessageLogs,
        modalId: "MessageLogs",
        columns: tableColumnArray,
        data, setData,
        dataMap,
        generateDataSlice,
        sortMap,
        dataCount, setDataCount,
        totalDataCount,
        setSortBy,
        sortByCol,
        sortByAsc,
        success, setSuccess,
        error, setError,
        startIndex: startIndexForTable,
        selectedIds, setSelectedIds,
        searchValue,
        setSearchValue,
        tableRef,
        tableWrapperRef,
        dataLoaded,
        buttons
    };

    return (
        <>
            <MesReceiversModal 
                receivers={modalData} 
                onClose={() => setModalData([])} 
            />
            <MesTable {...tableProps} />
        </>
    );
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(MessageLogs);
